import { Card, CardContent } from "@/ui/base/card";
import { cn } from "@/nucleo/utilitarios";
import { type LucideIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface StatCardProps {
  title: string;
  value: string | number;
  description?: string;
  icon: LucideIcon;
  trend?: { value: number; label: string };
  className?: string;
  href?: string;
  onClick?: () => void;
  active?: boolean;
}

export function StatCard({ title, value, description, icon: Icon, trend, className, href, onClick, active }: StatCardProps) {
  const navigate = useNavigate();

  const interactive = !!href || !!onClick;
  const handleClick = () => {
    if (onClick) return onClick();
    if (href) navigate(href);
  };

  return (
    <Card
      className={cn(
        "group relative overflow-hidden rounded-2xl border border-border/60 bg-card shadow-xs transition-all duration-300",
        // Padrão global: hover de elevação SOMENTE quando o card é clicável (href/onClick).
        interactive && "cursor-pointer hover:shadow-md hover:scale-[1.02]",
        active && "ring-2 ring-primary/60 border-primary/40",
        className,
      )}
      onClick={interactive ? handleClick : undefined}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={interactive ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleClick(); } } : undefined}
    >
      {/* Subtle gradient overlay */}
      <div className="absolute inset-0 bg-linear-to-br from-primary/3 to-transparent pointer-events-none" />

      <CardContent className="relative p-3 sm:p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1.5 min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{title}</p>
            <p className="text-2xl font-extrabold font-mono tracking-tight text-foreground">{value}</p>
            {description && <p className="text-xs text-muted-foreground/80 leading-snug">{description}</p>}
            {trend && (
              <p className={cn(
                "text-xs font-semibold",
                trend.value >= 0 ? "text-success" : "text-destructive-text",
              )}>
                {trend.value >= 0 ? "↑" : "↓"} {Math.abs(trend.value)}% {trend.label}
              </p>
            )}
          </div>
          <div className={cn(
            "shrink-0 rounded-xl bg-orange/10 p-3 transition-transform duration-300",
            interactive && "group-hover:scale-105",
          )}>
            <Icon className="h-5 w-5 text-orange/80" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
